/* eslint-disable no-extra-boolean-cast */
import { type FC } from "react";
import ReactJson from "react-json-view";
import styled from "styled-components";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import {
  TextInput,
  TooltipText,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { ProductVariantFullApiType } from "../../../../util/types";
import { Column, FieldRow, Section } from "../../components/DetailsConstructor";
import { formatCurrency } from "../../../../util/data-utils";
import {
  CURRENCIES,
  CurrencyCodes,
  SegmentType,
} from "@shared_packages/toolkit/types";
import { InventoryLocationTable } from "./InventoryLocationTable";

interface ProductDetailsProps {
  data: ProductVariantFullApiType;
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  padding: 30px;
  @media (max-width: 767px) {
    padding: 14px;
  }
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
`;

const SubSection = styled.div<{ hasSegments?: boolean }>`
  display: grid;
  grid-template-columns: 100px 200px 1fr;
  ${({ hasSegments }) =>
    hasSegments &&
    "padding-bottom: 15px;border-bottom: 1px solid rgba(173, 181, 189, 0.224);"}
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 50px;
`;

const PricingSegments = styled.div``;

const Segment = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 22px;
`;

const ViewRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 20px;
`;

const FieldContainer = styled.div`
  flex: 1;
`;

const Tier = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const PricingTiers = styled.div``;

const StyledTextInput = styled(TextInput)`
  pointer-events: none;
`;

const ProductDetails: FC<ProductDetailsProps> = ({ data }) => {
  const { deliveryInfo, inventoryInfo, minOrderQty, maxOrderQty, pricing } =
    data;

  const existingCurrenciesInPricingKeys = Object.keys(pricing);

  return (
    <AnimateAppearanceWrapper>
      <Container>
        <ContentWrapper>
          <Typography variant={TypographyVariant.HEADER2}>General </Typography>
          <Section>
            <Column>
              <FieldRow
                label="Name"
                value={
                  data.parentProduct?.name +
                  " " +
                  data.productOptionConfig
                    .map((option) => option.optionValue)
                    .join(" /")
                }
              />
            </Column>
            <Column>
              <FieldRow label="Description" value={data.description} />
              <FieldRow
                label="Short Description"
                value={data.shortDescription}
              />
            </Column>
            <Column>
              <FieldRow
                label="Brand"
                value={data?.parentProduct?.brand?.name}
              />
              <FieldRow
                label="Supplier"
                value={data?.parentProduct?.supplier?.name}
              />
            </Column>
            <Column>
              <FieldRow label="Status" value={data.parentProduct?.status} />

              <FieldRow
                label="Published date"
                value={data?.parentProduct?.publishedDate?.toString()}
              />
            </Column>
          </Section>
          <Typography variant={TypographyVariant.HEADER2}>Pricing </Typography>
          <Section
            style={{
              display: "flex",
              flexDirection: "column",
              gridTemplateColumns: "none",
            }}
          >
            {existingCurrenciesInPricingKeys.map((key, i) => {
              const currency = CURRENCIES[key as CurrencyCodes];
              return (
                <>
                  <SubSection
                    hasSegments={
                      !!pricing[key as CurrencyCodes]?.segmentPricing?.length
                    }
                    key={key}
                    style={
                      i !== 0 ? { borderTop: "1px solid #adb5bd39" } : undefined
                    }
                  >
                    <TooltipText id={key + "tooltip"} text={currency?.name}>
                      <Typography variant={TypographyVariant.BODY5}>
                        ({currency.symbol}) {key}
                      </Typography>
                    </TooltipText>
                    <Column
                      style={{
                        borderRight: "1px solid #adb5bd39",
                        borderLeft: "1px solid #adb5bd39",
                        padding: "0 25px",
                      }}
                    >
                      <Typography variant={TypographyVariant.BODY5}>
                        Retail price
                      </Typography>
                      <FieldRow
                        label="Cost"
                        value={formatCurrency(
                          pricing[key as CurrencyCodes]?.cost,
                          2,
                          key as CurrencyCodes
                        )}
                      />
                      <FieldRow
                        label="Price excl. vat"
                        value={formatCurrency(
                          pricing[key as CurrencyCodes]?.price,
                          2,
                          key as CurrencyCodes
                        )}
                      />
                      <FieldRow
                        label="Vat %"
                        value={pricing[key as CurrencyCodes]?.VAT}
                      />
                      <FieldRow
                        label="Price incl. vat"
                        value={formatCurrency(
                          pricing[key as CurrencyCodes]?.priceWithVAT,
                          2,
                          key as CurrencyCodes
                        )}
                      />
                    </Column>
                    <Column style={{ padding: "0 25px" }}>
                      <Typography variant={TypographyVariant.BODY5}>
                        Wholesale pricing tiers
                      </Typography>
                      {!!pricing[key as CurrencyCodes]?.wholesaleTiers?.length
                        ? pricing[key as CurrencyCodes]?.wholesaleTiers?.map(
                            (tier, tierIndex, array) => (
                              <FlexRow key={tier.minQty.toString() + key}>
                                <FieldRow
                                  label={"Tier" + (i + 1).toString()}
                                  value={formatCurrency(
                                    tier.price,
                                    2,
                                    key as CurrencyCodes
                                  )}
                                />
                                <FieldRow
                                  label="Min items per order"
                                  value={tier.minQty}
                                />
                                <FieldRow
                                  label="Max items per order"
                                  value={
                                    tierIndex === array.length - 1 &&
                                    tier?.maxQty === Number.MAX_SAFE_INTEGER
                                      ? "-"
                                      : tier.maxQty
                                  }
                                />
                              </FlexRow>
                            )
                          )
                        : "N/a"}
                    </Column>
                  </SubSection>
                  {!!pricing[key as CurrencyCodes]?.segmentPricing?.length && (
                    <PricingSegments>
                      <Typography variant={TypographyVariant.BODY5}>
                        Segment pricing
                      </Typography>
                      {pricing[key as CurrencyCodes]?.segmentPricing
                        ?.sort((a, b) => {
                          if (b.segment.type === SegmentType.CUSTOMER) return 1;
                          return -1;
                        })
                        ?.map((item) => {
                          return (
                            <Segment key={item.segment._id}>
                              <ViewRow>
                                <FieldContainer>
                                  <StyledTextInput
                                    value={item.segment.name}
                                    label={
                                      item.segment.type === SegmentType.CUSTOMER
                                        ? "Customer segment"
                                        : "Location segment"
                                    }
                                  />
                                </FieldContainer>
                                <FieldContainer>
                                  <StyledTextInput
                                    value={Number(item.price).toFixed(2)}
                                    label="Retail price (exc. tax)"
                                  />
                                </FieldContainer>
                              </ViewRow>
                              {!!item.wholesaleTiers?.length && (
                                <PricingTiers>
                                  {item.wholesaleTiers?.map(
                                    (tier, tierIndex, array) => {
                                      return (
                                        <Tier
                                          key={
                                            (item.segment._id as string) +
                                            tier.price
                                          }
                                        >
                                          <ViewRow>
                                            <FieldContainer>
                                              <StyledTextInput
                                                value={Number(
                                                  tier.price
                                                ).toFixed(2)}
                                                label={
                                                  item.wholesaleTiers
                                                    ?.length === 1
                                                    ? "Default tier"
                                                    : `Tier ${tierIndex + 1}`
                                                }
                                              />
                                            </FieldContainer>
                                            <FieldContainer>
                                              <StyledTextInput
                                                value={tier.minQty}
                                                label="Min items per order"
                                              />
                                            </FieldContainer>
                                            <FieldContainer>
                                              <StyledTextInput
                                                value={
                                                  tierIndex ===
                                                    array.length - 1 &&
                                                  tier?.maxQty ===
                                                    Number.MAX_SAFE_INTEGER
                                                    ? "-"
                                                    : tier.maxQty
                                                }
                                                label="Max items per order"
                                              />
                                            </FieldContainer>
                                          </ViewRow>
                                        </Tier>
                                      );
                                    }
                                  )}
                                </PricingTiers>
                              )}
                            </Segment>
                          );
                        })}
                    </PricingSegments>
                  )}
                </>
              );
            })}
          </Section>

          <Section>
            <Column>
              <FieldRow
                label="Minimum retail order quantity"
                value={minOrderQty}
              />
            </Column>
            <Column>
              <FieldRow
                label="Maximum retail order quantity"
                value={maxOrderQty}
              />
            </Column>
          </Section>
          {/* Inventory info of product */}

          {inventoryInfo && (
            <>
              <Typography variant={TypographyVariant.HEADER2}>
                Inventory info
              </Typography>

              <Section withoutBottomLine>
                <Column>
                  <FieldRow label="SKU" value={data.sku} />
                  <FieldRow label="Stock status" value={inventoryInfo.status} />
                  <FieldRow label="Stock quantity" value={inventoryInfo.qty} />
                  {/* <FieldRow
                    label="Committed quantity"
                    value={inventoryInfo.committedQty}
                  />
                  <FieldRow
                    label="Reserved quantity"
                    value={inventoryInfo.reservedQty}
                  />
                  <FieldRow
                    label="Incoming quantity"
                    value={inventoryInfo.incomingQty}
                  /> */}
                </Column>
                <Column>
                  <FieldRow
                    label="Barcode"
                    value={
                      !!inventoryInfo.barcode ? inventoryInfo.barcode : "-"
                    }
                  />
                  <FieldRow
                    label="is sellable"
                    value={
                      (data.parentProduct as any)?.isSellable ? "yes" : "no"
                    }
                  />
                  <FieldRow
                    label="is trackable"
                    value={
                      (data.parentProduct as any)?.isTrackable ? "yes" : "no"
                    }
                  />
                  {/* <FieldRow label="room" value={inventoryInfo.room} />
                  <FieldRow label="shelf" value={inventoryInfo.shelf} /> */}
                </Column>
              </Section>
              <div
                style={{
                  borderBottom: "1px solid #adb5bd",
                  paddingBottom: 20,
                  marginBottom: 20,
                }}
              >
                <InventoryLocationTable
                  stockLocations={data.inventoryInfo?.stockLocations}
                  isTrackableWatch={(data.parentProduct as any)?.isTrackable}
                />
              </div>
            </>
          )}
          {/* Delivery info of product */}

          {deliveryInfo && (
            <>
              <Typography variant={TypographyVariant.HEADER2}>
                Delivery info
              </Typography>

              <Section>
                <Column>
                  <FieldRow
                    label="Delivery time"
                    value={deliveryInfo.deliveryTime}
                  />
                  <FieldRow label="Delivery cost" value={deliveryInfo.height} />
                  <FieldRow
                    label="Delivery unit"
                    value={deliveryInfo.heightUnit}
                  />
                </Column>

                <Column>
                  <FieldRow
                    label="Delivery weight"
                    value={deliveryInfo.weight}
                  />
                  <FieldRow
                    label="Delivery weight unit"
                    value={deliveryInfo.weightUnit}
                  />
                </Column>

                <Column>
                  <FieldRow label="Delivery width" value={deliveryInfo.width} />
                  <FieldRow
                    label="Delivery width unit"
                    value={deliveryInfo.widthUnit}
                  />
                </Column>
              </Section>
            </>
          )}

          <Typography variant={TypographyVariant.HEADER2}>Raw data </Typography>
          <Section>
            <Column>
              <ReactJson
                src={data}
                collapsed
                enableClipboard
                name={"product variant"}
              />
            </Column>
          </Section>
        </ContentWrapper>
      </Container>
    </AnimateAppearanceWrapper>
  );
};

export default ProductDetails;
